import { useCallback, useRef } from "react"

import { Button } from "@atoms/index"

import Dialog from "@molecules/Dialog/Dialog"

import styles from "./BillUnapplyVendorCreditDialog.module.scss"
import { BillUnapplyVendorCreditDialogProps } from "./BillUnapplyVendorCreditDialog.types"

export default function BillUnapplyVendorCreditDialog(props: BillUnapplyVendorCreditDialogProps) {
    const { isOpen, onOpenChange, vendorCreditNumber, amount, isHeldByQuickBooksDesktop, isUnapplying, onConfirm } =
        props

    // Store onOpenChange in a ref so the callback identity stays stable across the legacy card's re-renders.
    // The Popover's useEffect depends on onOpenChange, and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // A successful removal reloads the page; the button stays busy until then so a second click cannot fire again.
    const handleConfirm = () => {
        if (!isUnapplying) {
            onConfirm()
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Unapply Vendor Credit" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {isHeldByQuickBooksDesktop ? (
                            <>
                                <p className={styles.paragraph}>
                                    QuickBooks Desktop holds Vendor Credit {vendorCreditNumber} ({amount}) against this
                                    bill, so it can only be removed here after it&apos;s released there.
                                </p>
                                <p className={styles.paragraph}>
                                    Mark it for removal, then release it from the bill in QuickBooks Desktop. The next
                                    QuickBooks sync removes it here.
                                </p>
                            </>
                        ) : (
                            <p className={styles.paragraph}>
                                Unapply Vendor Credit {vendorCreditNumber} ({amount}) from this bill? The bill will owe
                                that amount again.
                            </p>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button variant="ghost" size="lg" type="button" onClick={() => stableOnOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        size="lg"
                        type="button"
                        onClick={handleConfirm}
                        isProcessing={isUnapplying}
                        isDisabled={isUnapplying}
                    >
                        {isHeldByQuickBooksDesktop ? "Mark for Removal" : "Unapply"}
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
